// 音频处理服务 - Vue3 TypeScript版本

import { log, sendBinaryMessage } from './websocket'

// =============================
// 类型定义
// =============================

interface OpusDecoderModule {
  _opus_decoder_get_size: (channels: number) => number
  _opus_decoder_init: (decoder: number, sampleRate: number, channels: number) => number
  _opus_decode: (
    decoder: number,
    data: number,
    len: number,
    pcm: number,
    frameSize: number,
    decodeFec: number
  ) => number
  _opus_encoder_get_size: (channels: number) => number
  _opus_encoder_init: (encoder: number, sampleRate: number, channels: number, application: number) => number
  _opus_encode: (
    encoder: number,
    pcm: number,
    frameSize: number,
    data: number,
    maxDataBytes: number
  ) => number
  _malloc: (size: number) => number
  _free: (ptr: number) => void
  HEAPU8: Uint8Array
  HEAP16: Int16Array
}

interface OpusDecoder {
  channels: number
  rate: number
  frameSize: number
  module: OpusDecoderModule
  decoderPtr: number | null
  init: () => boolean
  decode: (opusData: Uint8Array) => Int16Array
  destroy: () => void
  extractDecodedData?: (mod: OpusDecoderModule, pcmPtr: number, decodedSamples: number) => Int16Array
}

interface OpusEncoder {
  channels: number
  rate: number
  frameSize: number
  module: OpusDecoderModule
  encoderPtr: number | null
  init: () => boolean
  encode: (pcmData: Int16Array) => Uint8Array | null
  destroy: () => void
}

interface AudioConfig {
  sampleRate: number
  channels: number
  frameSize: number
}

function getPlaybackConfig(profile: PlaybackProfile): AudioConfig {
  if (profile === 'call') {
    return {
      sampleRate: 16000,
      channels: 1,
      frameSize: 960 // 60ms @ 16kHz
    }
  }

  return {
    sampleRate: PLAYBACK_CONFIG.sampleRate,
    channels: PLAYBACK_CONFIG.channels,
    frameSize: PLAYBACK_CONFIG.frameSize
  }
}

interface StreamingContext {
  queue: number[]
  playing: boolean
  hasScheduledOnce: boolean
  endOfStream: boolean
  source: AudioBufferSourceNode | null
  sources?: Set<AudioBufferSourceNode>
  schedulerTimer?: number | null
  nextStartTime?: number
  totalSamples: number
  lastPlayTime: number
  analyser: AnalyserNode | null
  decodeOpusFrames: (opusFrames: Uint8Array[]) => Promise<void>
  startPlaying: () => void
}

declare global {
  interface Window {
    Module?: any
    ModuleInstance?: OpusDecoderModule
    audioContext?: AudioContext
    streamingContext?: StreamingContext
    enableAudio?: () => Promise<boolean>
    recordingContext?: RecordingContext
  }
}

// 录音上下文
interface RecordingContext {
  mediaStream: MediaStream | null
  audioContext: AudioContext | null
  workletNode: AudioWorkletNode | null
  sourceNode: MediaStreamAudioSourceNode | null
  isRecording: boolean
  pcmBuffer: Int16Array[]
  encoder: OpusEncoder | null
  profile: RecordingProfile
}

type RecordingProfile = 'dialogue' | 'call'

interface StartRecordingOptions {
  profile?: RecordingProfile
}

interface StopRecordingOptions {
  flushFinalFrame?: boolean
}

type PlaybackProfile = 'dialogue' | 'call'

interface InitAudioOptions {
  playbackProfile?: PlaybackProfile
}

// =============================
// 配置
// =============================

const PLAYBACK_CONFIG = {
  sampleRate: 24000, // TTS 使用 24kHz
  channels: 1,
  frameSize: 1440 // 60ms @ 24kHz
}

const RECORDING_CONFIG = {
  sampleRate: 16000, // STT/ASR 使用 16kHz
  channels: 1,
  frameSize: 960 // 60ms @ 16kHz
}

const CALL_RECORDING_CONFIG = {
  sampleRate: 16000,
  channels: 1,
  frameSize: 960 // 60ms @ 16kHz
}

const CALL_PLAYBACK_MAX_BUFFERED_SEC = 0.18
const CALL_PLAYBACK_MAX_OPUS_FRAMES = 3
const REALTIME_DEBUG_HEAD_BYTES = 8
const DIALOGUE_RECORDING_PROCESSOR_BUFFER_SIZE = 4096
const CALL_RECORDING_PROCESSOR_BUFFER_SIZE = 1024
const CALL_UPLINK_SILENCE_RMS_THRESHOLD = 180
const CALL_UPLINK_SILENCE_PEAK_THRESHOLD = 1200
const CALL_UPLINK_SILENCE_HANGOVER_FRAMES = 6

// =============================
// 状态变量
// =============================

let audioContext: AudioContext | null = null
let opusDecoder: OpusDecoder | null = null
let audioBufferQueue: Uint8Array[] = []
let isAudioBuffering = false
let isAudioPlaying = false
let streamingContext: StreamingContext | null = null
let audioContextResumePromise: Promise<AudioContext> | null = null
let decodePumpScheduled = false
let decodingInProgress = false
let endOfStreamTimer: number | null = null
let playbackProfile: PlaybackProfile = 'dialogue'
let lastCallLatencyTrimLogAt = 0
let callUplinkSilentFrameCount = 0
let callUplinkDroppedSilenceFrameCount = 0

function formatAudioBytesForDebug(data: Uint8Array | ArrayBuffer, maxBytes = REALTIME_DEBUG_HEAD_BYTES): string {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data)
  return Array.from(bytes.slice(0, Math.min(maxBytes, bytes.length)))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join(' ')
}

function shouldLogRealtimeFrame(frameNo: number): boolean {
  return frameNo <= 5 || frameNo % 50 === 0
}

function trimQueueHead<T>(queue: T[], maxItems: number): number {
  const overflow = queue.length - maxItems
  if (overflow <= 0) {
    return 0
  }
  queue.splice(0, overflow)
  return overflow
}

function trimCallPlaybackSampleQueue(queue: number[], sampleRate: number): number {
  const maxSamples = Math.max(960, Math.floor(sampleRate * CALL_PLAYBACK_MAX_BUFFERED_SEC))
  return trimQueueHead(queue, maxSamples)
}

function trimCallPlaybackFrameQueue(queue: Uint8Array[]): number {
  return trimQueueHead(queue, CALL_PLAYBACK_MAX_OPUS_FRAMES)
}

function getRecordingProcessorBufferSize(profile: RecordingProfile): number {
  return profile === 'call'
    ? CALL_RECORDING_PROCESSOR_BUFFER_SIZE
    : DIALOGUE_RECORDING_PROCESSOR_BUFFER_SIZE
}

function getMicrophoneAudioConstraints(profile: RecordingProfile): MediaTrackConstraints {
  const isCall = profile === 'call'
  return {
    channelCount: 1,
    sampleRate: { ideal: 16000 },
    echoCancellation: true,
    noiseSuppression: !isCall,
    autoGainControl: !isCall
  }
}

function getPcmStats(pcmData: Int16Array): { peak: number; rms: number } {
  if (pcmData.length === 0) {
    return { peak: 0, rms: 0 }
  }

  let peak = 0
  let squareSum = 0
  for (let i = 0; i < pcmData.length; i++) {
    const value = pcmData[i] ?? 0
    const abs = Math.abs(value)
    if (abs > peak) {
      peak = abs
    }
    squareSum += value * value
  }

  return {
    peak,
    rms: Math.sqrt(squareSum / pcmData.length)
  }
}

function resetCallUplinkSilenceGate(): void {
  callUplinkSilentFrameCount = 0
  callUplinkDroppedSilenceFrameCount = 0
}

function shouldSendCallUplinkFrame(pcmData: Int16Array): boolean {
  const stats = getPcmStats(pcmData)
  const active = stats.peak >= CALL_UPLINK_SILENCE_PEAK_THRESHOLD ||
    stats.rms >= CALL_UPLINK_SILENCE_RMS_THRESHOLD

  if (active) {
    callUplinkSilentFrameCount = 0
    return true
  }

  callUplinkSilentFrameCount++
  if (callUplinkSilentFrameCount <= CALL_UPLINK_SILENCE_HANGOVER_FRAMES) {
    return true
  }

  callUplinkDroppedSilenceFrameCount++
  return false
}

function logCallLatencyTrim(message: string): void {
  const now = Date.now()
  if (now - lastCallLatencyTrimLogAt < 3000) {
    return
  }
  lastCallLatencyTrimLogAt = now
  log(message, 'warning')
}

export const __testOnly = {
  trimCallPlaybackSampleQueue,
  trimCallPlaybackFrameQueue,
  formatAudioBytesForDebug,
  shouldLogRealtimeFrame,
  getRecordingProcessorBufferSize,
  getMicrophoneAudioConstraints,
  resetCallUplinkSilenceGate,
  shouldSendCallUplinkFrame
}

// =============================
// 音频上下文初始化
// =============================

async function initAudioContext(): Promise<AudioContext | null> {
  if (audioContext) {
    if (audioContext.state === 'suspended' && !audioContextResumePromise) {
      audioContextResumePromise = new Promise((resolve) => {
        log('音频上下文已暂停。需要用户交互才能恢复。', 'warning')

        const resumeAudioContext = async () => {
          try {
            if (audioContext) {
              await audioContext.resume()
              log('音频上下文已通过用户交互恢复', 'success')
              resolve(audioContext)

              ;['click', 'touchstart', 'keydown'].forEach(event => {
                document.removeEventListener(event, resumeAudioContext)
              })
              audioContextResumePromise = null
            }
          } catch (err) {
            log('恢复音频上下文失败: ' + err, 'error')
          }
        }

        ;['click', 'touchstart', 'keydown'].forEach(event => {
          document.addEventListener(event, resumeAudioContext, { once: false })
        })
      })

      return audioContextResumePromise
    }
    return audioContext
  }

  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
    audioContext = new AudioContextClass({
      sampleRate: PLAYBACK_CONFIG.sampleRate,
      latencyHint: 'interactive'
    })

    if (audioContext.state === 'suspended') {
      log('新创建的音频上下文处于暂停状态。需要用户交互才能启动。', 'warning')

      audioContextResumePromise = new Promise((resolve) => {
        const resumeAudioContext = async () => {
          try {
            if (audioContext) {
              await audioContext.resume()
              log('音频上下文已通过用户交互启动', 'success')
              resolve(audioContext)

              ;['click', 'touchstart', 'keydown'].forEach(event => {
                document.removeEventListener(event, resumeAudioContext)
              })
              audioContextResumePromise = null
            }
          } catch (err) {
            log('启动音频上下文失败: ' + err, 'error')
          }
        }

        ;['click', 'touchstart', 'keydown'].forEach(event => {
          document.addEventListener(event, resumeAudioContext, { once: false })
        })
      })

      return audioContextResumePromise
    }

    window.audioContext = audioContext

    return audioContext
  } catch (error) {
    log('初始化音频上下文失败:' + error, 'error')
    return null
  }
}

// =============================
// Opus 库加载
// =============================

function checkOpusLoaded(): boolean {
  try {
    if (typeof window.Module === 'undefined') {
      return false
    }

    if (
      typeof window.Module.instance !== 'undefined' &&
      typeof window.Module.instance._opus_decoder_get_size === 'function'
    ) {
      window.ModuleInstance = window.Module.instance
      log('Opus库加载成功（使用Module.instance）', 'success')
      return true
    }

    if (typeof window.Module._opus_decoder_get_size === 'function') {
      window.ModuleInstance = window.Module
      log('Opus库加载成功（使用全局Module）', 'success')
      return true
    }

    if (
      window.ModuleInstance &&
      typeof window.ModuleInstance._opus_decoder_get_size === 'function'
    ) {
      log('Opus库已加载（使用ModuleInstance）', 'success')
      return true
    }

    return false
  } catch (err) {
    log(`Opus库检查失败: ${err}`, 'error')
    return false
  }
}

export function loadOpusLibrary(): Promise<boolean> {
  return new Promise(resolve => {
    if (checkOpusLoaded()) {
      resolve(true)
      return
    }

    log('尝试加载libopus.js', 'info')

    const baseUrl = import.meta.env.BASE_URL || '/'
    const basePath = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/'

    const possiblePaths = [
      `${basePath}libopus.js`,
      `${basePath}js/libopus.js`,
      `${basePath}static/js/libopus.js`,
      '/libopus.js',
      './libopus.js'
    ]

    const script = document.createElement('script')
    script.async = true

    script.onload = () => {
      log('libopus.js脚本加载成功，等待初始化', 'success')

      const maxAttempts = 100
      let attempts = 0

      const checkModule = () => {
        if (checkOpusLoaded()) {
          log('Opus库初始化成功', 'success')
          resolve(true)
          return
        }

        if (attempts >= maxAttempts) {
          log('Opus库初始化超时', 'error')
          resolve(false)
          return
        }

        attempts++
        setTimeout(checkModule, 100)
      }

      checkModule()
    }

    script.onerror = () => {
      log('libopus.js加载失败，尝试下一个路径', 'warning')
      tryNextPath()
    }

    let pathIndex = 0

    function tryNextPath() {
      if (pathIndex >= possiblePaths.length) {
        log('所有路径都尝试失败', 'error')
        resolve(false)
        return
      }

      const path = possiblePaths[pathIndex]
      pathIndex++

      log(`尝试从路径加载: ${path}`, 'info')
      script.src = path!
      document.head.appendChild(script)
    }

    tryNextPath()
  })
}

// =============================
// Opus 解码器
// =============================

function createOpusDecoder(mod: OpusDecoderModule): OpusDecoder {
  try {
    const playbackConfig = getPlaybackConfig(playbackProfile)
    const SAMPLE_RATE = playbackConfig.sampleRate
    const CHANNELS = playbackConfig.channels
    const FRAME_SIZE = playbackConfig.frameSize

    const decoder: OpusDecoder = {
      channels: CHANNELS,
      rate: SAMPLE_RATE,
      frameSize: FRAME_SIZE,
      module: mod,
      decoderPtr: null,

      init: function () {
        if (this.decoderPtr) return true

        const decoderSize = mod._opus_decoder_get_size(this.channels)
        log('Opus解码器大小:' + decoderSize + '字节', 'debug')

        this.decoderPtr = mod._malloc(decoderSize)
        if (!this.decoderPtr) {
          throw new Error('无法分配解码器内存')
        }

        const err = mod._opus_decoder_init(this.decoderPtr, this.rate, this.channels)

        if (err < 0) {
          this.destroy()
          throw new Error(`Opus解码器初始化失败: ${err}`)
        }

        log('Opus解码器初始化成功', 'success')
        return true
      },

      decode: function (opusData: Uint8Array): Int16Array {
        if (!this.decoderPtr) {
          if (!this.init()) {
            throw new Error('解码器未初始化且无法初始化')
          }
        }

        // 添加数据有效性检查
        if (!opusData || opusData.length === 0) {
          log('Opus解码失败: 输入数据为空', 'warning')
          return new Int16Array(0)
        }

        // 打印数据头以便调试
        ;

        // 检查数据是否可能有效（Opus帧通常不会太大）
        if (opusData.length > 1275) { // Opus最大帧大小
          log(`Opus解码警告: 输入数据过大 (${opusData.length} 字节)`, 'warning')
        }

        try {
          const mod = this.module

          const opusPtr = mod._malloc(opusData.length)
          mod.HEAPU8.set(opusData, opusPtr)

          // 调整解码缓冲区大小以匹配新的帧大小
          const maxFrameSize = this.frameSize * 6  // 5760 样本，支持最大 120ms
          const pcmPtr = mod._malloc(maxFrameSize * 2)

          const decodedSamples = mod._opus_decode(
            this.decoderPtr!,
            opusPtr,
            opusData.length,
            pcmPtr,
            maxFrameSize,  // 使用更大的最大帧大小
            0
          )

          // 释放 opusPtr，因为不再需要它
          mod._free(opusPtr)

          if (decodedSamples < 0) {
            mod._free(pcmPtr)
            const headLen = Math.min(16, opusData.length)
            const headerHex = Array.from(opusData.slice(0, headLen))
              .map(b => b.toString(16).padStart(2, '0'))
              .join(' ')
            const headerAscii = Array.from(opusData.slice(0, headLen))
              .map(b => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
              .join('')

            log(
              `Opus解码失败: 错误码=${decodedSamples}, 输入大小=${opusData.length}, 头hex=${headerHex}, 头ascii=${headerAscii}`,
              'error'
            )
            
            // 添加更详细的错误信息
            const errorMsg = getOpusErrorMessage(decodedSamples)
            
            // 对于某些错误，尝试重置解码器
            if (decodedSamples === -4) { // Invalid packet
              log('检测到无效数据包，尝试重置解码器', 'warning')
              this.destroy()
              if (this.init()) {
                // 重新分配内存并尝试解码
                const retryOpusPtr = mod._malloc(opusData.length)
                mod.HEAPU8.set(opusData, retryOpusPtr)
                const retryPcmPtr = mod._malloc(maxFrameSize * 2)
                
                const retryResult = mod._opus_decode(
                  this.decoderPtr!,
                  retryOpusPtr,
                  opusData.length,
                  retryPcmPtr,
                  maxFrameSize,
                  0
                )
                
                mod._free(retryOpusPtr)
                
                if (retryResult >= 0) {
                  log('重置解码器后解码成功', 'success')
                  return extractDecodedData(mod, retryPcmPtr, retryResult)
                } else {
                  mod._free(retryPcmPtr)
                  log(`重试解码也失败: 错误码=${retryResult}`, 'error')
                }
              }
            }
            
            throw new Error(`Opus解码失败: ${errorMsg} (错误码: ${decodedSamples})`)
          }

          log(`Opus解码成功: 输入=${opusData.length}字节, 输出=${decodedSamples}样本`, 'debug')
          return extractDecodedData(mod, pcmPtr, decodedSamples)
        } catch (error) {
          log('Opus解码错误:' + error, 'error')
          return new Int16Array(0)
        }
      },

      destroy: function () {
        if (this.decoderPtr) {
          this.module._free(this.decoderPtr)
          this.decoderPtr = null
        }
      }
    }

    if (!decoder.init()) {
      throw new Error('Opus解码器初始化失败')
    }

    opusDecoder = decoder
    return decoder
  } catch (error) {
    log('Opus解码器初始化失败:' + error, 'error')
    opusDecoder = null
    throw error
  }
}

export async function initOpusDecoder(): Promise<OpusDecoder | null> {
  if (opusDecoder) {
    return opusDecoder
  }

  try {
    const opusLoaded = await loadOpusLibrary()
    if (!opusLoaded) {
      throw new Error('Opus库未加载')
    }

    let mod = window.ModuleInstance
    if (!mod && checkOpusLoaded()) {
      mod = window.ModuleInstance
    }

    if (!mod) {
      throw new Error('ModuleInstance不可用')
    }

    return createOpusDecoder(mod)
  } catch (error) {
    log(`初始化Opus解码器失败:` + error, 'error')
    throw error
  }
}

// =============================
// 音频播放
// =============================

function convertInt16ToFloat32(int16Data: Int16Array): number[] {
  const out = Array.from({ length: int16Data.length }, () => 0)
  for (let i = 0; i < int16Data.length; i++) {
    const v = int16Data[i]!
    out[i] = v / (v < 0 ? 0x8000 : 0x7fff)
  }
  return out
}

function resetAudioBuffer(): void {
  audioBufferQueue = []
  isAudioBuffering = false
  isAudioPlaying = false
}

async function pumpDecodeFrames(): Promise<void> {
  if (!streamingContext || !isAudioPlaying) return
  if (decodingInProgress) return
  if (audioBufferQueue.length === 0) return

  decodingInProgress = true
  try {
    const maxFramesPerPump = 6
    const frames = audioBufferQueue.splice(0, maxFramesPerPump)
    await streamingContext.decodeOpusFrames(frames)
  } finally {
    decodingInProgress = false

    if (audioBufferQueue.length > 0 && streamingContext && isAudioPlaying) {
      scheduleDecodePump()
    }
  }
}

function scheduleDecodePump(): void {
  if (decodePumpScheduled) return
  decodePumpScheduled = true
  queueMicrotask(() => {
    decodePumpScheduled = false
    void pumpDecodeFrames()
  })
}

function addAudioToBuffer(opusData: Uint8Array): boolean {
  audioBufferQueue.push(opusData)

  if (playbackProfile === 'call') {
    const droppedFrames = trimCallPlaybackFrameQueue(audioBufferQueue)
    if (droppedFrames > 0) {
      logCallLatencyTrim(`通话播放积压，丢弃最旧的 ${droppedFrames} 个 Opus 帧以控制延迟`)
    }
  }

  if (isAudioPlaying && streamingContext) {
    scheduleDecodePump()
    return true
  }

  if (!isAudioBuffering && !isAudioPlaying) {
    startAudioBuffering()
  }

  return true
}

function startAudioBuffering(): boolean {
  if (isAudioBuffering || isAudioPlaying) return false

  isAudioBuffering = true
  log('开始音频缓冲...', 'info')

  initOpusDecoder().catch(error => {
    log(`预初始化Opus解码器失败: ${error}`, 'warning')
  })

  setTimeout(() => {
    if (isAudioBuffering && audioBufferQueue.length > 0) {
      log(`缓冲超时，当前缓冲包数: ${audioBufferQueue.length}，开始播放`, 'info')
      playBufferedAudio()
    }
  }, 150)

  // 减少缓冲阈值以更快开始播放
  const bufferThreshold = 1
  const bufferCheckInterval = setInterval(() => {
    if (!isAudioBuffering) {
      clearInterval(bufferCheckInterval)
      return
    }

    if (audioBufferQueue.length >= bufferThreshold) {
      clearInterval(bufferCheckInterval)
      log(`已缓冲 ${audioBufferQueue.length} 个音频包，开始播放`, 'info')
      playBufferedAudio()
    }
  }, 30)

  return true
}

async function playBufferedAudio(): Promise<boolean> {
  console.log(`🎵 playBufferedAudio 被调用 - isAudioPlaying: ${isAudioPlaying}, queueLength: ${audioBufferQueue.length}`)
  if (isAudioPlaying || audioBufferQueue.length === 0) return false

  isAudioPlaying = true
  isAudioBuffering = false

  try {
    if (!audioContext) {
      console.log('⚠️ audioContext 不存在，正在初始化...')
      audioContext = await initAudioContext()
    }

    // 尝试恢复被暂停的 AudioContext
    if (audioContext && audioContext.state === 'suspended') {
      console.log(`⚠️ AudioContext 处于 suspended 状态，需要用户交互才能播放`)
      log('尝试恢复音频上下文...', 'info')
      try {
        await audioContext.resume()
        console.log('✅ 音频上下文已恢复')
        log('音频上下文已恢复', 'success')
      } catch (e) {
        console.log('❌ 恢复音频上下文失败:', e)
        log('恢复音频上下文失败，等待用户交互...', 'warning')
        audioContext = await initAudioContext()
        if (!audioContext || audioContext.state === 'suspended') {
          console.log('❌ AudioContext 仍处于 suspended 状态，无法播放')
          isAudioPlaying = false
          return false
        }
      }
    }

    if (!audioContext) {
      console.log('❌ 音频上下文初始化失败')
      log('音频上下文初始化失败', 'error')
      isAudioPlaying = false
      return false
    }

    console.log(`🎵 音频上下文状态: ${audioContext.state}`)
    log(`音频上下文状态: ${audioContext.state}`, 'info')

    if (!opusDecoder) {
      log('初始化Opus解码器...', 'info')
      try {
        opusDecoder = await initOpusDecoder()
        if (!opusDecoder) {
          throw new Error('解码器初始化失败')
        }
        log('Opus解码器初始化成功', 'success')
      } catch (error) {
        log('Opus解码器初始化失败: ' + error, 'error')
        isAudioPlaying = false
        return false
      }
    }

    if (!streamingContext) {
        const playbackConfig = getPlaybackConfig(playbackProfile)
        streamingContext = {
          queue: [],
          playing: false,
          hasScheduledOnce: false,
          endOfStream: false,
          source: null,
          sources: new Set(),
          schedulerTimer: null,
          nextStartTime: 0,
        totalSamples: 0,
        lastPlayTime: 0,
        analyser: null,

        decodeOpusFrames: async function (opusFrames: Uint8Array[]) {
          if (!opusDecoder) {
            try {
              opusDecoder = await initOpusDecoder()
            } catch (error) {
              log('Opus解码器未初始化，无法解码: ' + error, 'error')
              return
            }
          }

          if (!opusDecoder) {
            log('Opus解码器未初始化，无法解码', 'error')
            return
          }

          const decodedSamples: number[] = []
          for (const frame of opusFrames) {
            try {
              const frameData = opusDecoder.decode(frame)
              if (frameData && frameData.length > 0) {
                const floatData = convertInt16ToFloat32(frameData)
                for (let i = 0; i < floatData.length; i++) {
                  decodedSamples.push(floatData[i]!)
                }
              }
            } catch (error) {
              log('Opus解码失败: ' + error, 'error')
            }
          }

          if (decodedSamples.length > 0) {
            for (let i = 0; i < decodedSamples.length; i++) {
              this.queue.push(decodedSamples[i]!)
            }
            if (playbackProfile === 'call') {
              const droppedSamples = trimCallPlaybackSampleQueue(this.queue, playbackConfig.sampleRate)
              if (droppedSamples > 0) {
                const droppedMs = Math.round((droppedSamples / playbackConfig.sampleRate) * 1000)
                logCallLatencyTrim(`通话播放积压，丢弃约 ${droppedMs}ms 的最旧 PCM 数据以控制延迟`)
              }
            }
            this.totalSamples += decodedSamples.length

            const startBufferSec = playbackProfile === 'call' ? 0.04 : 0.16
            const minSamples = playbackConfig.sampleRate * startBufferSec
            if (!this.playing && this.queue.length >= minSamples) {
              this.startPlaying()
            }
          } else {
            log('没有成功解码的样本', 'warning')
          }
        },

        startPlaying: function () {
          if (this.playing || this.queue.length === 0 || !audioContext) return

          // 尝试恢复 suspended 的 AudioContext
          if (audioContext.state === 'suspended') {
            initAudioContext().then(ctx => {
              if (ctx && ctx.state !== 'suspended') {
                log('音频上下文已恢复，开始播放', 'success')
                this.startPlaying()
              } else {
                log('音频上下文仍处于暂停状态，无法播放', 'warning')
              }
            })
            return
          }

          const isCallPlayback = playbackProfile === 'call'
          const scheduleChunkSec = isCallPlayback ? 0.06 : 0.12
          const startBufferSec = isCallPlayback ? 0.04 : 0.16
          const lookAheadSec = isCallPlayback ? 0.12 : 0.8
          const schedulerIntervalMs = isCallPlayback ? 20 : 40
          const chunkSamples = Math.max(240, Math.floor(playbackConfig.sampleRate * scheduleChunkSec))
          const minChunkSamples = isCallPlayback
            ? Math.max(240, Math.floor(playbackConfig.sampleRate * 0.02))
            : Math.max(240, playbackConfig.frameSize)
          const startThresholdSamples = Math.max(chunkSamples, Math.floor(playbackConfig.sampleRate * startBufferSec))
          const fadeInDuration = 0.006
          const fadeOutDuration = 0.008

          const ensureTimer = () => {
            if (this.schedulerTimer != null) return
            this.schedulerTimer = window.setInterval(() => {
              if (!audioContext || !this.playing) return

              if (this.queue.length < startThresholdSamples && audioBufferQueue.length > 0) {
                scheduleDecodePump()
              }

              const now = audioContext.currentTime
              const base = this.nextStartTime && this.nextStartTime > 0 ? this.nextStartTime : now + 0.03
              let startTime = Math.max(now + 0.02, base)

              const shouldStart = this.hasScheduledOnce
                ? this.queue.length >= minChunkSamples
                : this.queue.length >= startThresholdSamples

              if (!shouldStart) {
                if (this.endOfStream && this.sources && this.sources.size === 0 && this.queue.length === 0 && audioBufferQueue.length === 0) {
                  if (this.schedulerTimer != null) {
                    clearInterval(this.schedulerTimer)
                    this.schedulerTimer = null
                  }
                  log('音频播放完成', 'info')
                  isAudioPlaying = false
                  streamingContext = null
                  window.streamingContext = undefined
                }
                return
              }

              const maxScheduleTime = now + lookAheadSec
              while (startTime < maxScheduleTime && this.queue.length >= minChunkSamples) {
                const takeSamples = this.queue.length >= chunkSamples ? chunkSamples : this.queue.length
                if (takeSamples < minChunkSamples) break

                const samples = this.queue.splice(0, takeSamples)
                const isFirstChunk = !this.sources || this.sources.size === 0
                const isLastChunk = this.endOfStream && this.queue.length === 0 && audioBufferQueue.length === 0
                const audioBuffer = audioContext.createBuffer(
                  playbackConfig.channels,
                  samples.length,
                  playbackConfig.sampleRate
                )

                const channelData = audioBuffer.getChannelData(0)
                for (let i = 0; i < samples.length; i++) {
                  channelData[i] = samples[i]!
                }

                const src = audioContext.createBufferSource()
                src.buffer = audioBuffer

                const gainNode = audioContext.createGain()
                const duration = audioBuffer.duration
                const endTime = startTime + duration

                if (isFirstChunk) {
                  gainNode.gain.setValueAtTime(0, startTime)
                  gainNode.gain.linearRampToValueAtTime(1, startTime + fadeInDuration)
                } else {
                  gainNode.gain.setValueAtTime(1, startTime)
                }

                if (isLastChunk && duration > fadeOutDuration) {
                  gainNode.gain.setValueAtTime(1, Math.max(startTime, endTime - fadeOutDuration))
                  gainNode.gain.linearRampToValueAtTime(0, endTime)
                }

                const analyserNode = audioContext.createAnalyser()
                analyserNode.fftSize = 256
                analyserNode.smoothingTimeConstant = 0.8

                src.connect(analyserNode)
                analyserNode.connect(gainNode)
                gainNode.connect(audioContext.destination)

                this.analyser = analyserNode
                this.source = src

                if (this.sources) {
                  this.sources.add(src)
                }

                if (!this.hasScheduledOnce) {
                  this.hasScheduledOnce = true
                }

                src.onended = () => {
                  if (this.sources) {
                    this.sources.delete(src)
                  }
                  if (this.source === src) {
                    this.source = null
                  }
                  if (this.sources && this.sources.size === 0) {
                    this.analyser = null
                  }

                  if (this.endOfStream && this.sources && this.sources.size === 0 && this.queue.length === 0 && audioBufferQueue.length === 0) {
                    if (this.schedulerTimer != null) {
                      clearInterval(this.schedulerTimer)
                      this.schedulerTimer = null
                    }
                    log('音频播放完成', 'info')
                    isAudioPlaying = false
                    streamingContext = null
                    window.streamingContext = undefined
                  }
                }

                try {
                  src.start(startTime)
                } catch {
                  if (this.sources) {
                    this.sources.delete(src)
                  }
                }

                this.lastPlayTime = startTime
                startTime += duration
                this.nextStartTime = startTime
              }
            }, schedulerIntervalMs)
          }

          this.playing = true
          this.nextStartTime = Math.max(audioContext.currentTime + 0.03, this.nextStartTime || 0)
          ensureTimer()
        }
      }

      window.streamingContext = streamingContext
    }

    scheduleDecodePump()
    return true
  } catch (error) {
    log(`播放已缓冲的音频出错:` + error, 'error')
    isAudioPlaying = false
    streamingContext = null
    window.streamingContext = undefined

    return false
  }
}

export function stopAudioPlayback(): boolean {
  try {
    isAudioPlaying = false
    isAudioBuffering = false
    playbackProfile = 'dialogue'
    lastCallLatencyTrimLogAt = 0

    if (endOfStreamTimer) {
      clearTimeout(endOfStreamTimer)
      endOfStreamTimer = null
    }

    if (streamingContext && streamingContext.source) {
      try {
        streamingContext.source.stop()
        streamingContext.source = null
        streamingContext.analyser = null
      } catch {
        // 忽略已停止的音频源错误
      }
    }

    if (streamingContext && streamingContext.schedulerTimer != null) {
      clearInterval(streamingContext.schedulerTimer)
      streamingContext.schedulerTimer = null
    }

    if (streamingContext && streamingContext.sources) {
      for (const src of streamingContext.sources) {
        try {
          src.stop()
        } catch {
          // ignore
        }
      }
      streamingContext.sources.clear()
    }

    audioBufferQueue = []
    streamingContext = null

    window.streamingContext = undefined

    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('audio-playback-stopped'))
    }

    log('音频播放已停止', 'info')
    return true
  } catch (error) {
    log(`停止音频播放失败:` + error, 'error')
    return false
  }
}

// =============================
// 导出函数
// =============================

export async function initAudio(options: InitAudioOptions = {}): Promise<boolean> {
  try {
    const nextPlaybackProfile = options.playbackProfile ?? 'dialogue'
    if (playbackProfile !== nextPlaybackProfile) {
      stopAudioPlayback()
      if (opusDecoder && opusDecoder.destroy) {
        opusDecoder.destroy()
        opusDecoder = null
      }
    }
    playbackProfile = nextPlaybackProfile
    await initAudioContext()

    window.enableAudio = async function () {
      try {
        if (audioContext && audioContext.state === 'suspended') {
          await audioContext.resume()
          log('音频上下文已恢复', 'success')
        }

        let opusLoaded = false
        for (let i = 0; i < 3; i++) {
          try {
            opusLoaded = await loadOpusLibrary()
            if (opusLoaded) {
              log(`Opus库加载成功 (尝试 ${i + 1}/3)`, 'success')
              break
            }
          } catch {
            log(`尝试 ${i + 1}/3 加载libopus.js失败，将重试`, 'warning')
          }
        }

        if (!opusLoaded) {
          log('所有Opus库加载尝试均失败，音频播放功能将不可用', 'error')
          return false
        }

        try {
          await initOpusDecoder()
          log('Opus解码器初始化成功', 'success')
          return true
        } catch (err) {
          log(`Opus解码器初始化失败: ${err}，音频播放功能将不可用`, 'error')
          return false
        }
      } catch (error) {
        log('启用音频失败:' + error, 'error')
        return false
      }
    }

    await loadOpusLibrary()

    log(`音频系统已初始化。playbackProfile=${playbackProfile}，请通过用户交互启用音频功能。`, 'info')

    return true
  } catch (error) {
    log('初始化音频失败:' + error, 'error')
    return false
  }
}

// 用于检测重复处理的计数器
let audioMessageCounter = 0

export async function handleBinaryAudioMessage(data: ArrayBuffer): Promise<boolean> {
  try {
    audioMessageCounter++
    const msgId = audioMessageCounter
    // 使用 warning 级别确保日志能显示
    console.log(`🔊 handleBinaryAudioMessage #${msgId} 被调用，数据大小: ${data.byteLength}字节`)
    log(`收到ArrayBuffer音频数据 #${msgId}，大小: ${data.byteLength}字节`, 'warning')

    const rawBytes = new Uint8Array(data)
    const unwrap = unwrapOpusFromMaybeJson(rawBytes)
    if (unwrap.action === 'ignore') {
      return true
    }
    const opusData = unwrap.action === 'use' ? unwrap.data : rawBytes

    if (opusData.length > 0) {
      if (endOfStreamTimer) {
        clearTimeout(endOfStreamTimer)
        endOfStreamTimer = null
      }

      addAudioToBuffer(opusData)

      if (window.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent('audio-data-received', {
            detail: { dataLength: opusData.length }
          })
        )
      }

      return true
    } else {
      log('收到空音频数据帧，可能是结束标志', 'warning')

      if (audioBufferQueue.length > 0 && !isAudioPlaying) {
        playBufferedAudio()
      }

      if (endOfStreamTimer) {
        clearTimeout(endOfStreamTimer)
      }

      endOfStreamTimer = window.setTimeout(() => {
        endOfStreamTimer = null
        if (isAudioPlaying && streamingContext) {
          streamingContext.endOfStream = true
        }
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('audio-playback-ended'))
        }
      }, 600)

      return true
    }
  } catch (error) {
    log('处理二进制消息出错:' + error, 'error')
    return false
  }
}

function unwrapOpusFromMaybeJson(
  bytes: Uint8Array
): { action: 'raw' } | { action: 'use'; data: Uint8Array } | { action: 'ignore' } {
  const first = firstNonWhitespaceByte(bytes)
  if (first !== 0x7b) {
    return { action: 'raw' }
  }

  try {
    const text = new TextDecoder().decode(bytes)
    const parsed = JSON.parse(text) as any

    const dataField = parsed?.data ?? parsed?.audioData ?? parsed?.audio_data
    if (!dataField) {
      log('收到JSON格式二进制数据，但未包含音频字段，已忽略', 'warning')
      return { action: 'ignore' }
    }

    if (typeof dataField === 'string') {
      const decoded = base64ToUint8Array(dataField)
      if (decoded && decoded.length > 0) {
        log(`收到JSON封装音频帧，解包后大小: ${decoded.length}字节`, 'info')
      }
      return { action: 'use', data: decoded }
    }

    if (dataField?.type === 'Buffer' && Array.isArray(dataField?.data)) {
      return { action: 'use', data: new Uint8Array(dataField.data) }
    }

    if (Array.isArray(dataField)) {
      return { action: 'use', data: new Uint8Array(dataField) }
    }

    return { action: 'ignore' }
  } catch {
    return { action: 'raw' }
  }
}

function firstNonWhitespaceByte(bytes: Uint8Array): number | null {
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]!
    if (b === 0x20 || b === 0x0a || b === 0x0d || b === 0x09) continue
    return b
  }
  return null
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

export function cleanupAudio(): boolean {
  try {
    stopAudioPlayback()

    if (opusDecoder && opusDecoder.destroy) {
      opusDecoder.destroy()
      opusDecoder = null
    }

    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close()
      audioContext = null
    }

    resetAudioBuffer()

    log('音频资源已清理', 'info')
    return true
  } catch (error) {
    log('清理音频资源失败:' + error, 'error')
    return false
  }
}

export function getAudioState() {
  return {
    isAudioBuffering,
    isAudioPlaying,
    audioBufferQueue,
    analyser: streamingContext && streamingContext.analyser
  }
}

// =============================
// Opus 编码器
// =============================

let opusEncoder: OpusEncoder | null = null

// OPUS_APPLICATION_VOIP = 2048
const OPUS_APPLICATION_VOIP = 2048

function createOpusEncoder(mod: OpusDecoderModule): OpusEncoder {
  try {
    // 录音上传使用 16kHz（匹配 ASR）
    const SAMPLE_RATE = RECORDING_CONFIG.sampleRate
    const CHANNELS = RECORDING_CONFIG.channels
    const FRAME_SIZE = RECORDING_CONFIG.frameSize
    const MAX_OPUS_SIZE = 1275

    const encoder: OpusEncoder = {
      channels: CHANNELS,
      rate: SAMPLE_RATE,
      frameSize: FRAME_SIZE,
      module: mod,
      encoderPtr: null,

      init: function () {
        if (this.encoderPtr) return true

        const encoderSize = mod._opus_encoder_get_size(this.channels)
        log('Opus编码器大小:' + encoderSize + '字节', 'debug')

        this.encoderPtr = mod._malloc(encoderSize)
        if (!this.encoderPtr) {
          throw new Error('无法分配编码器内存')
        }

        const err = mod._opus_encoder_init(
          this.encoderPtr,
          this.rate,
          this.channels,
          OPUS_APPLICATION_VOIP
        )

        if (err < 0) {
          this.destroy()
          throw new Error(`Opus编码器初始化失败: ${err}`)
        }

        log('Opus编码器初始化成功', 'success')
        return true
      },

      encode: function (pcmData: Int16Array): Uint8Array | null {
        if (!this.encoderPtr) {
          if (!this.init()) {
            throw new Error('编码器未初始化且无法初始化')
          }
        }

        try {
          const mod = this.module

          // 分配 PCM 缓冲区
          const pcmPtr = mod._malloc(pcmData.length * 2)
          for (let i = 0; i < pcmData.length; i++) {
            mod.HEAP16[(pcmPtr >> 1) + i] = pcmData[i]!
          }

          // 分配 Opus 输出缓冲区
          const opusPtr = mod._malloc(MAX_OPUS_SIZE)

          const encodedBytes = mod._opus_encode(
            this.encoderPtr!,
            pcmPtr,
            this.frameSize,
            opusPtr,
            MAX_OPUS_SIZE
          )

          if (encodedBytes < 0) {
            mod._free(pcmPtr)
            mod._free(opusPtr)
            throw new Error(`Opus编码失败: ${encodedBytes}`)
          }

          const encodedData = new Uint8Array(encodedBytes)
          for (let i = 0; i < encodedBytes; i++) {
            encodedData[i] = mod.HEAPU8[opusPtr + i]!
          }

          mod._free(pcmPtr)
          mod._free(opusPtr)

          return encodedData
        } catch (error) {
          log('Opus编码错误:' + error, 'error')
          return null
        }
      },

      destroy: function () {
        if (this.encoderPtr) {
          this.module._free(this.encoderPtr)
          this.encoderPtr = null
        }
      }
    }

    if (!encoder.init()) {
      throw new Error('Opus编码器初始化失败')
    }

    opusEncoder = encoder
    return encoder
  } catch (error) {
    log('Opus编码器初始化失败:' + error, 'error')
    opusEncoder = null
    throw error
  }
}

export async function initOpusEncoder(): Promise<OpusEncoder | null> {
  if (opusEncoder) {
    return opusEncoder
  }

  try {
    const opusLoaded = await loadOpusLibrary()
    if (!opusLoaded) {
      throw new Error('Opus库未加载')
    }

    let mod = window.ModuleInstance
    if (!mod && checkOpusLoaded()) {
      mod = window.ModuleInstance
    }

    if (!mod) {
      throw new Error('ModuleInstance不可用')
    }

    return createOpusEncoder(mod)
  } catch (error) {
    log(`初始化Opus编码器失败:` + error, 'error')
    throw error
  }
}

// =============================
// 麦克风录音
// =============================

let recordingContext: RecordingContext | null = null
let pcmAccumulator: number[] = []
let callUplinkFrameCounter = 0

// AudioWorklet 处理器代码
const _audioWorkletCode = `
class AudioRecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.isRecording = true
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0]
    if (input && input[0] && this.isRecording) {
      // 发送音频数据到主线程
      this.port.postMessage({
        type: 'audio',
        data: input[0]
      })
    }
    return this.isRecording
  }
}

registerProcessor('audio-recorder-processor', AudioRecorderProcessor)
`

// 将浮点音频转换为 16 位 PCM
function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length)
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]!))
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
  }
  return int16Array
}

// 对音频进行重采样
function resample(input: Float32Array, inputSampleRate: number, outputSampleRate: number): Float32Array {
  if (inputSampleRate === outputSampleRate) {
    return input
  }

  const ratio = inputSampleRate / outputSampleRate
  const outputLength = Math.round(input.length / ratio)
  const output = new Float32Array(outputLength)

  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio
    const srcIndexFloor = Math.floor(srcIndex)
    const srcIndexCeil = Math.min(srcIndexFloor + 1, input.length - 1)
    const fraction = srcIndex - srcIndexFloor
    output[i] = input[srcIndexFloor]! * (1 - fraction) + input[srcIndexCeil]! * fraction
  }

  return output
}

export async function startMicrophoneRecording(options: StartRecordingOptions = {}): Promise<boolean> {
  if (recordingContext && recordingContext.isRecording) {
    log('已经在录音中', 'warning')
    return true
  }

  try {
    const profile: RecordingProfile = options.profile ?? 'dialogue'
    const frameConfig = profile === 'call' ? CALL_RECORDING_CONFIG : RECORDING_CONFIG
    if (profile === 'call') {
      console.info(`[console-call] 开始麦克风录音: profile=${profile}, frameSize=${frameConfig.frameSize}`)
    }

    // 检查浏览器是否支持麦克风访问
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const isHttps = window.location.protocol === 'https:'
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      
      if (!isHttps && !isLocalhost) {
        const currentOrigin = window.location.origin
        throw new Error(
          `麦克风访问需要 HTTPS 环境。\n\n` +
          `解决方案：在 Chrome 地址栏输入 chrome://flags/#unsafely-treat-insecure-origin-as-secure\n` +
          `然后添加: ${currentOrigin}\n` +
          `启用后重启浏览器即可使用麦克风`
        )
      }
      throw new Error('您的浏览器不支持麦克风访问，请使用现代浏览器（Chrome/Firefox/Edge）')
    }

    // 初始化 Opus 编码器
    await initOpusEncoder()
    if (!opusEncoder) {
      throw new Error('Opus编码器初始化失败')
    }

    // 获取麦克风权限
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: getMicrophoneAudioConstraints(profile)
    })

    log('获取麦克风权限成功', 'success')

    // 创建音频上下文
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
    const recAudioContext = new AudioContextClass({
      sampleRate: 16000,
      latencyHint: 'interactive'
    })

    // 确保音频上下文已启动
    if (recAudioContext.state === 'suspended') {
      await recAudioContext.resume()
      log('麦克风录音音频上下文已恢复', 'info')
    }

    // 如果采样率不是 16000，需要记录以便重采样
    const actualSampleRate = recAudioContext.sampleRate
    log(`音频上下文采样率: ${actualSampleRate}`, 'info')
    if (profile === 'call') {
      const audioTrackSettings = mediaStream.getAudioTracks?.()[0]?.getSettings?.()
      console.info(
        `[console-call] audioContext sampleRate=${actualSampleRate}, ` +
          `trackSampleRate=${audioTrackSettings?.sampleRate ?? 'unknown'}, ` +
          `channelCount=${audioTrackSettings?.channelCount ?? 'unknown'}`
      )
    }

    // 创建源节点
    const sourceNode = recAudioContext.createMediaStreamSource(mediaStream)

    // 使用 ScriptProcessorNode（AudioWorklet 可能不受支持）
    const bufferSize = getRecordingProcessorBufferSize(profile)
    const scriptNode = recAudioContext.createScriptProcessor(bufferSize, 1, 1)

    // 初始化录音上下文
    recordingContext = {
      mediaStream,
      audioContext: recAudioContext,
      workletNode: null,
      sourceNode,
      isRecording: true,
      pcmBuffer: [],
      encoder: opusEncoder,
      profile
    }
    pcmAccumulator = []

    const FRAME_SIZE = frameConfig.frameSize
    if (profile === 'call') {
      callUplinkFrameCounter = 0
      resetCallUplinkSilenceGate()
    }

    scriptNode.onaudioprocess = (event) => {
      if (!recordingContext || !recordingContext.isRecording) return

      const inputData = event.inputBuffer.getChannelData(0)
      
      // 重采样到 16kHz
      const resampled = resample(inputData, actualSampleRate, 16000)
      
      // 转换为 16 位 PCM
      const pcmData = floatTo16BitPCM(resampled)
      
      // 累积 PCM 数据
      for (let i = 0; i < pcmData.length; i++) {
        pcmAccumulator.push(pcmData[i]!)
      }

      // 当累积够一帧时，编码并发送
      while (pcmAccumulator.length >= FRAME_SIZE) {
        const frameData = new Int16Array(pcmAccumulator.splice(0, FRAME_SIZE))
        
        if (opusEncoder) {
          if (profile === 'call' && !shouldSendCallUplinkFrame(frameData)) {
            if (shouldLogRealtimeFrame(callUplinkDroppedSilenceFrameCount)) {
              console.info(
                `[console-call-uplink] 静音帧已抑制: dropped=${callUplinkDroppedSilenceFrameCount}`
              )
            }
            continue
          }

          const encoded = opusEncoder.encode(frameData)
          if (encoded && encoded.length > 0) {
            // 通过 WebSocket 发送 Opus 帧
            const sent = sendBinaryMessage(encoded)
            if (profile === 'call') {
              callUplinkFrameCounter++
              if (shouldLogRealtimeFrame(callUplinkFrameCounter)) {
                console.info(
                  `[console-call-uplink] 发送Opus帧: ${encoded.length}字节 ` +
                    `#${callUplinkFrameCounter} head=${formatAudioBytesForDebug(encoded)} sent=${sent}`
                )
              }
            }
            if (!sent) {
              log(`发送Opus帧失败: ${encoded.length}字节`, 'warning')
            }
            log(`发送Opus帧: ${encoded.length}字节`, 'debug')
          }
        }
      }
    }

    // 连接节点
    sourceNode.connect(scriptNode)
    scriptNode.connect(recAudioContext.destination)

    // 保存 scriptNode 引用以便清理
    ;(recordingContext as any).scriptNode = scriptNode

    window.recordingContext = recordingContext

    log(`开始麦克风录音: profile=${profile}, frameSize=${FRAME_SIZE}`, 'success')
    return true
  } catch (error) {
    log('开始麦克风录音失败: ' + error, 'error')
    throw error
  }
}

export function stopMicrophoneRecording(options: StopRecordingOptions = {}): boolean {
  try {
    if (!recordingContext) {
      log('没有正在进行的录音', 'warning')
      return true
    }

    const flushFinalFrame = options.flushFinalFrame ?? true
    const activeProfile = recordingContext.profile
    const frameSize = activeProfile === 'call' ? CALL_RECORDING_CONFIG.frameSize : RECORDING_CONFIG.frameSize

    recordingContext.isRecording = false

    // 断开节点
    if ((recordingContext as any).scriptNode) {
      ;(recordingContext as any).scriptNode.disconnect()
    }

    if (recordingContext.sourceNode) {
      recordingContext.sourceNode.disconnect()
    }

    // 停止媒体流
    if (recordingContext.mediaStream) {
      recordingContext.mediaStream.getTracks().forEach(track => track.stop())
    }

    // 关闭音频上下文
    if (recordingContext.audioContext && recordingContext.audioContext.state !== 'closed') {
      recordingContext.audioContext.close()
    }

    // 处理剩余的累积数据
    if (flushFinalFrame && pcmAccumulator.length > 0 && opusEncoder) {
      log(`处理剩余的 ${pcmAccumulator.length} 个采样点`, 'debug')
      
      // 补零直到达到帧大小
      while (pcmAccumulator.length < frameSize) {
        pcmAccumulator.push(0)
      }
      
      // 只需要处理第一帧（通常剩余数据不足一帧）
      if (pcmAccumulator.length >= frameSize) {
        const frameData = new Int16Array(pcmAccumulator.slice(0, frameSize))
        const encoded = opusEncoder.encode(frameData)
        
        if (encoded && encoded.length > 0) {
          sendBinaryMessage(encoded)
          log(`发送最后一帧数据: ${encoded.length}字节`, 'debug')
        }
      }
    } else if (!flushFinalFrame && pcmAccumulator.length > 0) {
      log(`丢弃剩余的 ${pcmAccumulator.length} 个采样点，不发送尾帧`, 'debug')
    }

    // 清空累积器
    pcmAccumulator = []

    recordingContext = null
    window.recordingContext = undefined

    log(`停止麦克风录音: profile=${activeProfile}, flushFinalFrame=${flushFinalFrame}`, 'info')
    return true
  } catch (error) {
    log('停止麦克风录音失败: ' + error, 'error')
    return false
  }
}

export function isRecording(): boolean {
  return recordingContext !== null && recordingContext.isRecording
}

export function cleanupEncoder(): boolean {
  try {
    stopMicrophoneRecording()

    if (opusEncoder && opusEncoder.destroy) {
      opusEncoder.destroy()
      opusEncoder = null
    }

    log('Opus编码器已清理', 'info')
    return true
  } catch (error) {
    log('清理Opus编码器失败: ' + error, 'error')
    return false
  }
}

// 添加获取Opus错误信息的辅助函数
function getOpusErrorMessage(errorCode: number): string {
  switch (errorCode) {
    case -1:
      return "Bad argument"
    case -2:
      return "Buffer too small"
    case -3:
      return "Internal error"
    case -4:
      return "Invalid packet"
    case -5:
      return "Unimplemented"
    case -6:
      return "Invalid state"
    case -7:
      return "Memory allocation failed"
    default:
      return "Unknown error"
  }
}

// 提取解码后的数据到Int16Array（独立函数）
function extractDecodedData(mod: OpusDecoderModule, pcmPtr: number, decodedSamples: number): Int16Array {
  const decodedData = new Int16Array(decodedSamples)
  for (let i = 0; i < decodedSamples; i++) {
    decodedData[i] = mod.HEAP16[(pcmPtr >> 1) + i]!
  }
  mod._free(pcmPtr)
  return decodedData
}
